import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Toast, Popconfirm, Space, Image } from '@douyinfe/semi-ui';
import request from '../../utils/request';
import styles from './index.module.scss';
import { IconEdit, IconDelete, IconPlus, IconSearch, IconEyeOpened, IconChevronLeft, IconChevronRight } from '@douyinfe/semi-icons';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import FieldMultiValueInput from '../../components/FieldValueInput/FieldMultiValueInput';
import ObjSelector from '../../components/ObjSelector';

const { Option } = Select;

const statusOptions = [
    { label: '正常', value: 0 },
    { label: '隐藏', value: 1 },
];

const payStatusOptions = [
    { label: '全部', value: 0 },
    { label: '已支付', value: 1 },
    { label: '未支付', value: 2 },
];

const PAGE_SIZE = 20;

// 时间格式化函数
const formatTime = (timestamp) => {
    if (!timestamp || (timestamp == '0')) return '-';
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

// 解析表单内容为可读格式
const parseFormData = (dataStr, fields) => {
    if (!dataStr) return '-';
    try {
        const dataObj = JSON.parse(dataStr);
        if (dataObj.r && Array.isArray(dataObj.r)) {
            return dataObj.r.map(item => {
                const field = fields.find(f => f.n === item.k);
                const fieldName = field ? field.n : item.k;
                const fieldType = field ? field.t : '';

                // 特殊处理图片字段
                if (fieldType === 'img' && item.v) {
                    return {
                        type: 'img',
                        fieldName,
                        imageUrl: item.v,
                        displayText: `${fieldName}: [图片]`
                    };
                } else if (fieldType === 'imgs' && item.v) {
                    const imgUrls = item.v.split(',').filter(url => url.trim());
                    return {
                        type: 'imgs',
                        fieldName,
                        imageUrls: imgUrls,
                        displayText: `${fieldName}: [${imgUrls.length}张图片]`
                    };
                } else if (fieldType === 'sign' && item.v) {
                    return {
                        type: 'sign',
                        fieldName,
                        imageUrl: item.v,
                        displayText: `${fieldName}: [签名图片]`
                    };
                }

                return {
                    type: 'text',
                    fieldName,
                    value: item.v || '-',
                    displayText: `${fieldName}: ${item.v || '-'}`
                };
            });
        }
        return [{ type: 'text', fieldName: '原始数据', value: dataStr, displayText: dataStr }];
    } catch {
        return [{ type: 'text', fieldName: '原始数据', value: dataStr, displayText: dataStr }];
    }
};

// 解析表单内容为详细格式（用于详情弹框）
const parseFormDataDetail = (dataStr, fields) => {
    if (!dataStr) return [];
    try {
        const dataObj = JSON.parse(dataStr);
        if (dataObj.r && Array.isArray(dataObj.r)) {
            return dataObj.r.map(item => {
                const field = fields.find(f => f.n === item.k);
                const fieldName = field ? field.n : item.k;
                const fieldType = field ? field.t : '';

                return {
                    fieldName,
                    fieldType,
                    value: item.v || '-',
                    key: item.k
                };
            });
        }
        return [];
    } catch {
        return [];
    }
};

const FormSuRecords = () => {
    const [searchParams] = useSearchParams();
    const form_uid = searchParams.get('form_uid');
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchPayStatus, setSearchPayStatus] = useState(0);
    const [formInfo, setFormInfo] = useState(null);
    const [addRecordValues, setAddRecordValues] = useState({});
    const [addRecordLoading, setAddRecordLoading] = useState(false);
    const [fields, setFields] = useState([]);
    const [hasInitializedValues, setHasInitializedValues] = useState(false);
    const [quickEditRemark, setQuickEditRemark] = useState({ uid: null, value: '', visible: false });
    const [batchEditRemarkVisible, setBatchEditRemarkVisible] = useState(false);
    const [batchEditRemarkValue, setBatchEditRemarkValue] = useState('');
    const [batchEditRemarkLoading, setBatchEditRemarkLoading] = useState(false);
    const [batchEditRemarkStatus, setBatchEditRemarkStatus] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [currentDetailData, setCurrentDetailData] = useState(null);
    const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
    const [currentImages, setCurrentImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // 检查表单是否需要支付
    const isFormPaid = formInfo?.price && formInfo.price > 0;

    useEffect(() => {
        if (!form_uid) return;
        // 获取表单详情
        request.get('/go/sp/form/get_form/', { params: { uid: form_uid } })
            .then(res => {
                if (res.errno === 0) {
                    setFormInfo(res.data);
                    setFields(res.data?.content ? JSON.parse(res.data.content).fields || [] : []);
                } else {
                    Toast.error(res.errstr || '获取表单信息失败');
                }
            })
            .catch(() => Toast.error('网络错误'));
    }, [form_uid]);

    useEffect(() => {
        fetchList();
    }, []);

    const fetchList = async (pageNum = page, limit = PAGE_SIZE) => {
        if (!form_uid) return;
        setLoading(true);
        try {
            const res = await request.get('/go/sp/form/list_form_su_record/', {
                params: {
                    page: pageNum - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    pay_status: isFormPaid ? searchPayStatus : undefined,
                    form_uid,
                },
            });
            if (res.errno === 0) {
                setList(res.list || []);
                setTotal(res.total_cnt || 0);
                setPage(pageNum);
            } else {
                Toast.error(res.errstr || '获取数据失败');
            }
        } catch (e) {
            Toast.error('网络错误');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditing(null);
        setAddRecordValues({});
        setHasInitializedValues(false);
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditing(record);
        setHasInitializedValues(false);

        // 立即设置表单值
        if (fields.length > 0) {
            let values = { ...record };
            try {
                const dataObj = JSON.parse(record.data || '{}');
                if (dataObj.r && Array.isArray(dataObj.r)) {
                    dataObj.r.forEach(item => {
                        values[item.k] = item.v;
                    });
                }
            } catch { }
            values.su_uid = record.su_uid;
            values.sp_remark = record.sp_remark;
            setAddRecordValues(values);
            setHasInitializedValues(true);
        }

        setModalVisible(true);
    };

    useEffect(() => {
        if (modalVisible && fields.length > 0 && !hasInitializedValues) {
            if (editing) {
                // 只有在fields刚加载完成时才设置值，避免重复设置导致用户信息丢失
                // 检查是否已经设置了用户信息，如果已设置则不再覆盖
                const currentSuUid = addRecordValues.su_uid;
                let values = { ...editing };
                try {
                    const dataObj = JSON.parse(editing.data || '{}');
                    if (dataObj.r && Array.isArray(dataObj.r)) {
                        dataObj.r.forEach(item => {
                            values[item.k] = item.v;
                        });
                    }
                } catch { }
                values.su_uid = currentSuUid || editing.su_uid;
                values.sp_remark = editing.sp_remark;
                setAddRecordValues(values);
                setHasInitializedValues(true);
            } else {
                setAddRecordValues({});
                setHasInitializedValues(true);
            }
        }
        // eslint-disable-next-line
    }, [modalVisible, editing?.uid, fields]);

    const handleDelete = async (uid) => {
        try {
            const res = await request.post('/go/sp/form/delete_form_su_record/', { uids: String(uid) });
            if (res.errno === 0) {
                Toast.success('删除成功');
                fetchList();
            } else {
                Toast.error(res.errstr || '删除失败');
            }
        } catch (e) {
            Toast.error('网络错误');
        }
    };

    const handleBatchDelete = async () => {
        if (!selectedRowKeys.length) return;
        try {
            const res = await request.post('/go/sp/form/delete_form_su_record/', { uids: selectedRowKeys.join(',') });
            if (res.errno === 0) {
                Toast.success('批量删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(res.errstr || '批量删除失败');
            }
        } catch (e) {
            Toast.error('网络错误');
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await formApi.validate();
            let res;
            if (editing) {
                res = await request.post('/go/sp/form/edit_form_su_record/', { ...values, uid: editing.uid });
            } else {
                res = await request.post('/go/sp/form/add_form_su_record/', values);
            }
            if (res.errno === 0) {
                Toast.success(editing ? '编辑成功' : '新增成功');
                setModalVisible(false);
                fetchList();
            } else {
                Toast.error(res.errstr || '操作失败');
            }
        } catch (e) {
            // 校验失败
        }
    };

    const handleSearch = () => {
        fetchList(1, PAGE_SIZE);
    };

    const handleQuickEditRemark = async () => {
        if (!quickEditRemark.uid || !quickEditRemark.value.trim()) return;

        try {
            const res = await request.post('/go/sp/form/edit_form_su_record/', {
                uid: quickEditRemark.uid,
                sp_remark: quickEditRemark.value.trim()
            });
            if (res.errno === 0) {
                Toast.success('备注修改成功');
                setQuickEditRemark({ uid: null, value: '', visible: false });
                fetchList();
            } else {
                Toast.error(res.errstr || '修改失败');
            }
        } catch (e) {
            Toast.error('网络错误');
        }
    };

    const handleBatchEditRemark = async () => {
        if (!selectedRowKeys.length || !batchEditRemarkValue.trim()) return;
        setBatchEditRemarkLoading(true);
        try {
            const res = await request.post('/go/sp/form/batchedit_form_su_record/', {
                uids: selectedRowKeys.join(','),
                sp_remark: batchEditRemarkValue.trim(),
                status: batchEditRemarkStatus === null ? undefined : batchEditRemarkStatus,
            });
            if (res.errno === 0) {
                Toast.success('批量备注成功');
                setBatchEditRemarkVisible(false);
                setBatchEditRemarkValue('');
                setBatchEditRemarkStatus(null);
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(res.errstr || '批量备注失败');
            }
        } catch (e) {
            Toast.error('网络错误');
        } finally {
            setBatchEditRemarkLoading(false);
        }
    };

    const handleViewDetail = (record) => {
        setCurrentDetailData({
            record,
            formData: parseFormDataDetail(record.data, fields)
        });
        setDetailModalVisible(true);
    };

    // 处理图片预览
    const handleImagePreview = (images, startIndex = 0) => {
        setCurrentImages(images);
        setCurrentImageIndex(startIndex);
        setImagePreviewVisible(true);
    };

    // 处理图片点击
    const handleImageClick = (item) => {
        if (item.type === 'img' || item.type === 'sign') {
            handleImagePreview([item.imageUrl], 0);
        } else if (item.type === 'imgs') {
            handleImagePreview(item.imageUrls, 0);
        }
    };

    const columns = [
        { title: '用户', dataIndex: 'su', render: (text, record) => record.su?.name || record.su_uid, width: 120 },
        {
            title: '表单内容',
            dataIndex: 'data',
            render: (text, record) => {
                const parsedItems = parseFormData(text, fields);
                const displayText = parsedItems.map(item => item.displayText).join('\n');
                const truncatedText = displayText.length > 100 ? displayText.slice(0, 100) + '...' : displayText;

                return (
                    <div style={{ whiteSpace: 'pre-line' }} title={displayText}>
                        {parsedItems.map((item, index) => (
                            <div key={index} style={{ marginBottom: '4px' }}>
                                {item.type === 'img' || item.type === 'imgs' || item.type === 'sign' ? (
                                    <span
                                        style={{
                                            color: '#1890ff',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            ':hover': { color: '#40a9ff' }
                                        }}
                                        onClick={() => handleImageClick(item)}
                                    >
                                        {item.displayText}
                                    </span>
                                ) : (
                                    <span>{item.displayText}</span>
                                )}
                            </div>
                        ))}
                        {displayText.length > 100 && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => handleViewDetail(record)}
                                style={{ padding: 0, marginLeft: 8 }}
                            >
                                查看详情
                            </Button>
                        )}
                    </div>
                );
            },
            width: 400
        },
        { title: '状态', dataIndex: 'status', render: v => statusOptions.find(i => i.value === v)?.label, width: 80 },
        {
            title: '后台备注',
            dataIndex: 'sp_remark',
            ellipsis: true,
            width: 200,
            render: (text, record) => (
                <div
                    style={{
                        color: 'red',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid transparent',
                        ':hover': {
                            border: '1px solid #d9d9d9',
                            backgroundColor: '#f5f5f5'
                        }
                    }}
                    onClick={() => setQuickEditRemark({
                        uid: record.uid,
                        value: text || '',
                        visible: true
                    })}
                    title="点击修改备注"
                >
                    {text || '-'}
                </div>
            )
        },
        // 只有当表单需要支付时才显示支付状态和付款时间列
        ...(isFormPaid ? [
            {
                title: '付款时间',
                dataIndex: 'paid_time',
                width: 220,
                render: (v, record) => {
                    let n = null, p = null;
                    try {
                        if (record.data) {
                            const dataObj = JSON.parse(record.data);
                            if (typeof dataObj.n !== 'undefined') n = dataObj.n;
                            if (typeof dataObj.p !== 'undefined') p = dataObj.p;
                        }
                    } catch { }
                    return (
                        <div>
                            <div>{formatTime(v)}</div>
                            {(n !== null || p !== null) && (
                                <div style={{ color: '#888', fontSize: 12 }}>
                                    {n && <span>选择: {n}</span>}
                                    {n && p && <span>，</span>}
                                    {p && <span>费用: ￥{p / 100}</span>}
                                </div>
                            )}
                        </div>
                    );
                }
            }
        ] : []),
        {
            title: '创建/更新时间',
            dataIndex: 'create_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTime(text)}</div>
                    <div>{formatTime(record.update_time)}</div>
                </div>
            )
        },
        {
            title: '操作', dataIndex: 'op', width: 200, render: (_, record) => (
                <Space>
                    <Button size="small" icon={<IconEyeOpened />} onClick={() => handleViewDetail(record)} style={{ marginRight: 8 }}>查看详情</Button>
                    <Button size="small" icon={<IconEdit />} onClick={() => handleEdit(record)} style={{ marginRight: 8 }}>编辑</Button>
                    <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.uid)}>
                        <Button size="small" type="danger" icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        },
    ];

    return (
        <div className={styles.formSuRecordsContainer}>
            {!form_uid ? (
                <div style={{ color: 'red', padding: 32 }}>缺少表单ID</div>
            ) : (
                <div className={styles.formTitle} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Link
                            to={`/Form?typ=${formInfo?.typ || 0}`}
                            style={{
                                color: '#1890ff',
                                textDecoration: 'none',
                                fontSize: '18px',
                                fontWeight: 'bold'
                            }}
                        >
                            {formInfo?.title || form_uid}
                        </Link>
                        <span style={{
                            color: '#666',
                            fontSize: '14px',
                            backgroundColor: '#f5f5f5',
                            padding: '4px 8px',
                            borderRadius: '4px'
                        }}>
                            总提交数量: {total}
                        </span>
                    </div>
                </div>
            )}
            {form_uid && (
                <>
                    <div className={styles.header}>
                        <Space>
                            <Input
                                placeholder="搜索内容/备注"
                                value={searchKey}
                                onChange={setSearchKey}
                                style={{ width: 200 }}
                                showClear
                            />
                            <Select
                                placeholder="状态"
                                value={searchStatus}
                                onChange={setSearchStatus}
                                style={{ width: 120 }}
                                showClear
                            >
                                <Select.Option value={null}>全部状态</Select.Option>
                                {statusOptions.map(opt => (
                                    <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                                ))}
                            </Select>
                            {isFormPaid && (
                                <Select
                                    placeholder="支付状态"
                                    value={searchPayStatus}
                                    onChange={setSearchPayStatus}
                                    style={{ width: 120 }}
                                >
                                    {payStatusOptions.map(opt => (
                                        <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                                    ))}
                                </Select>
                            )}
                            <Button
                                type="primary"
                                icon={<IconSearch />}
                                onClick={handleSearch}
                                loading={loading}
                            >
                                搜索
                            </Button>
                            <Button type="primary" icon={<IconPlus />} onClick={handleAdd}>新增</Button>
                            {selectedRowKeys.length > 0 && (
                                <>
                                    <Popconfirm title="确定批量删除？" onConfirm={handleBatchDelete}>
                                        <Button type="danger" icon={<IconDelete />}>批量删除</Button>
                                    </Popconfirm>
                                    <Button
                                        type="warning"
                                        onClick={() => setBatchEditRemarkVisible(true)}
                                        style={{ marginLeft: 8 }}
                                    >
                                        批量编辑备注
                                    </Button>
                                </>
                            )}
                        </Space>
                    </div>
                    <Table
                        rowKey="uid"
                        columns={columns}
                        dataSource={list}
                        loading={loading}
                        pagination={{
                            currentPage: page,
                            pageSize: PAGE_SIZE,
                            total,
                            onPageChange: (p) => fetchList(p, PAGE_SIZE),
                        }}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: setSelectedRowKeys,
                        }}
                        size="middle"
                    />
                    <Modal
                        title={editing ? '编辑表单数据' : '新增表单数据'}
                        visible={modalVisible}
                        onCancel={() => setModalVisible(false)}
                        footer={null}
                        centered
                        width={700}
                        bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
                    >
                        <Form
                            key={editing ? `edit-${editing.uid}` : 'add-new'}
                            initValues={addRecordValues}
                            values={addRecordValues}
                            onSubmit={async () => {
                                setAddRecordLoading(true);
                                try {
                                    // 组装data
                                    const dataArr = fields.map(f => ({ k: f.n, v: addRecordValues[f.n] }));
                                    let res;
                                    if (editing) {
                                        res = await request.post('/go/sp/form/edit_form_su_record/', {
                                            uid: editing.uid,
                                            form_uid,
                                            su_uid: addRecordValues.su_uid || '',
                                            data: JSON.stringify({ r: dataArr }),
                                            status: addRecordValues.status ?? 0,
                                            sp_remark: addRecordValues.sp_remark || '',
                                        });
                                    } else {
                                        res = await request.post('/go/sp/form/add_form_su_record/', {
                                            form_uid,
                                            su_uid: addRecordValues.su_uid || '',
                                            data: JSON.stringify({ r: dataArr }),
                                            status: addRecordValues.status ?? 0,
                                            sp_remark: addRecordValues.sp_remark || '',
                                        });
                                    }
                                    if (res.errno === 0) {
                                        Toast.success(editing ? '编辑成功' : '新增成功');
                                        setModalVisible(false);
                                        fetchList();
                                    } else {
                                        Toast.error(res.errstr || '操作失败');
                                    }
                                } catch {
                                    Toast.error('操作失败');
                                } finally {
                                    setAddRecordLoading(false);
                                }
                            }}
                            style={{ padding: 20 }}
                        >
                            <FieldMultiValueInput
                                fields={fields}
                                value={addRecordValues}
                                onChange={newValues => {
                                    // 保留原有的非表单字段（如su_uid、sp_remark等）
                                    setAddRecordValues(prev => ({
                                        ...prev,
                                        ...newValues
                                    }));
                                }}
                                nameKey="n"
                            />
                            <div style={{ margin: '16px 0' }}>
                                <label style={{ display: 'block', marginBottom: 8 }}>用户</label>
                                <ObjSelector
                                    value={addRecordValues.su_uid}
                                    onChange={v => setAddRecordValues(vals => ({ ...vals, su_uid: Array.isArray(v) ? v[0] : v }))}
                                    placeholder="请选择用户"
                                    single={true}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <Form.Input
                                field="sp_remark"
                                label="备注"
                                value={addRecordValues.sp_remark}
                                onChange={v => setAddRecordValues(vals => ({ ...vals, sp_remark: v }))}
                            />
                            <div style={{ textAlign: 'right', marginTop: 24 }}>
                                <Space>
                                    <Button onClick={() => setModalVisible(false)}>取消</Button>
                                    <Button type="primary" htmlType="submit" loading={addRecordLoading}>确定</Button>
                                </Space>
                            </div>
                        </Form>
                    </Modal>

                    {/* 快捷修改备注弹窗 */}
                    <Modal
                        title="快捷修改备注"
                        visible={quickEditRemark.visible}
                        onCancel={() => setQuickEditRemark({ uid: null, value: '', visible: false })}
                        onOk={handleQuickEditRemark}
                        centered
                        width={400}
                    >
                        <Input
                            placeholder="请输入备注"
                            value={quickEditRemark.value}
                            onChange={value => setQuickEditRemark(prev => ({ ...prev, value }))}
                            style={{ marginTop: 16 }}
                        />
                    </Modal>

                    <Modal
                        title="批量编辑备注"
                        visible={batchEditRemarkVisible}
                        onCancel={() => { setBatchEditRemarkVisible(false); setBatchEditRemarkValue(''); setBatchEditRemarkStatus(null); }}
                        onOk={handleBatchEditRemark}
                        confirmLoading={batchEditRemarkLoading}
                        centered
                        width={400}
                    >
                        <Input
                            placeholder="请输入备注"
                            value={batchEditRemarkValue}
                            onChange={setBatchEditRemarkValue}
                            style={{ marginTop: 16 }}
                        />
                        <Select
                            placeholder="请选择状态"
                            value={batchEditRemarkStatus}
                            onChange={setBatchEditRemarkStatus}
                            style={{ width: '100%', marginTop: 16 }}
                            showClear
                        >
                            <Select.Option value={null}>全部状态</Select.Option>
                            {statusOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                            ))}
                        </Select>
                    </Modal>

                    {/* 详情弹框 */}
                    <Modal
                        title="内容详情"
                        visible={detailModalVisible}
                        onCancel={() => setDetailModalVisible(false)}
                        footer={null}
                        centered
                        width={800}
                        bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                    >
                        {currentDetailData && (
                            <div style={{ padding: 20 }}>
                                <div style={{ marginBottom: 20 }}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isFormPaid ? '1fr 1fr 1fr' : '1fr 1fr 1fr 1fr',
                                        gap: '12px',
                                        fontSize: '14px'
                                    }}>
                                        <div>{currentDetailData.record.su?.name || currentDetailData.record.su_uid}</div>
                                        <div>{statusOptions.find(i => i.value === currentDetailData.record.status)?.label}</div>
                                        <div>
                                            <div>创建: {formatTime(currentDetailData.record.create_time)}</div>
                                            <div>更新: {formatTime(currentDetailData.record.update_time)}</div>
                                        </div>
                                        <div>{currentDetailData.record.sp_remark || '-'}</div>
                                        {isFormPaid ? (
                                            <>
                                                <div><strong>付款时间:</strong> {formatTime(currentDetailData.record.paid_time)}</div>
                                            </>
                                        ) : null}
                                    </div>
                                </div>

                                <div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {currentDetailData.formData.map((item, index) => (
                                            <div key={index} style={{
                                                border: '1px solid #f0f0f0',
                                                borderRadius: '6px',
                                                padding: '12px',
                                                backgroundColor: '#fafafa',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '12px'
                                            }}>
                                                <div style={{
                                                    fontWeight: 'bold',
                                                    color: '#333',
                                                    fontSize: '14px',
                                                    minWidth: '120px',
                                                    flexShrink: 0
                                                }}>
                                                    {item.fieldName}:
                                                </div>
                                                <div style={{
                                                    fontSize: '14px',
                                                    lineHeight: '1.4',
                                                    flex: 1
                                                }}>
                                                    {item.fieldType === 'img' && item.value !== '-' ? (
                                                        <div>
                                                            <Image
                                                                src={item.value}
                                                                width={200}
                                                                height={150}
                                                                style={{
                                                                    objectFit: 'cover',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer'
                                                                }}
                                                                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                                                                onClick={() => handleImagePreview([item.value], 0)}
                                                            />
                                                        </div>
                                                    ) : item.fieldType === 'imgs' && item.value !== '-' ? (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                            {item.value.split(',').filter(url => url.trim()).map((imgUrl, imgIndex) => (
                                                                <Image
                                                                    key={imgIndex}
                                                                    src={imgUrl.trim()}
                                                                    width={120}
                                                                    height={90}
                                                                    style={{
                                                                        objectFit: 'cover',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                                                                    onClick={() => handleImagePreview(
                                                                        item.value.split(',').filter(url => url.trim()),
                                                                        imgIndex
                                                                    )}
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : item.fieldType === 'sign' && item.value !== '-' ? (
                                                        <div>
                                                            <Image
                                                                src={item.value}
                                                                width={200}
                                                                height={100}
                                                                style={{
                                                                    objectFit: 'contain',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid #d9d9d9',
                                                                    cursor: 'pointer'
                                                                }}
                                                                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                                                                onClick={() => handleImagePreview([item.value], 0)}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            color: '#666'
                                                        }}>
                                                            {item.value}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal>

                    {/* 图片预览弹框 */}
                    <Modal
                        title="图片预览"
                        visible={imagePreviewVisible}
                        onCancel={() => setImagePreviewVisible(false)}
                        footer={null}
                        centered
                        width={800}
                        bodyStyle={{ padding: 0, textAlign: 'center' }}
                    >
                        {currentImages.length > 0 && (
                            <div style={{ position: 'relative' }}>
                                <div style={{
                                    position: 'absolute',
                                    top: '10px',
                                    left: '10px',
                                    zIndex: 1,
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}>
                                    {currentImageIndex + 1} / {currentImages.length}
                                </div>

                                <Image
                                    src={currentImages[currentImageIndex]}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '70vh',
                                        objectFit: 'contain'
                                    }}
                                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                                />

                                {currentImages.length > 1 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '10px',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1
                                    }}>
                                        <Button
                                            type="primary"
                                            shape="circle"
                                            icon={<IconChevronLeft />}
                                            onClick={() => setCurrentImageIndex(prev =>
                                                prev > 0 ? prev - 1 : currentImages.length - 1
                                            )}
                                            style={{
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                border: 'none'
                                            }}
                                        />
                                    </div>
                                )}

                                {currentImages.length > 1 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '10px',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1
                                    }}>
                                        <Button
                                            type="primary"
                                            shape="circle"
                                            icon={<IconChevronRight />}
                                            onClick={() => setCurrentImageIndex(prev =>
                                                prev < currentImages.length - 1 ? prev + 1 : 0
                                            )}
                                            style={{
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                border: 'none'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </Modal>
                </>
            )}
        </div>
    );
};

export default FormSuRecords; 